import { assertNever } from "@redotech/util/type";

export enum MerchantEmailTrigger {
  DISPUTE_MANUAL_EDIT = "dispute_manual_edit",
  DISPUTE_ONBOARDING_COMPLETE = "dispute_onboarding_complete",
  DISPUTE_ONBOARDING_BILLING_INCOMPLETE = "dispute_onboarding_billing_incomplete",
  DISPUTE_ONBOARDING_AUTOMATION_DECLINED = "dispute_onboarding_automation_declined",
}

export enum MerchantEmailRecipientType {
  TEAM_OWNER = "team_owner",
  NOTIFICATION_EMAIL = "notification_email",
  ALL_TEAM_ADMINS = "all_team_admins",
}

export enum MerchantEmailFrequency {
  EVERY_TIME = "every_time",
  COOLDOWN = "cooldown",
  ONCE_PER_TEAM = "once_per_team",
}

export enum MerchantEmailCampaignStatus {
  NEW = "new",
  ACTIVE = "active",
  ARCHIVED = "archived",
}

export function getMerchantEmailTriggerMeta(trigger: MerchantEmailTrigger): {
  label: string;
  description: string;
} {
  switch (trigger) {
    case MerchantEmailTrigger.DISPUTE_MANUAL_EDIT:
      return {
        label: "Dispute Manual Edit",
        description:
          "Merchant uploaded or edited dispute evidence in Shopify instead of through Redo.",
      };
    case MerchantEmailTrigger.DISPUTE_ONBOARDING_COMPLETE:
      return {
        label: "Dispute Onboarding Complete",
        description:
          "Merchant completed dispute onboarding with automation enabled. Encourage full integration.",
      };
    case MerchantEmailTrigger.DISPUTE_ONBOARDING_BILLING_INCOMPLETE:
      return {
        label: "Dispute Onboarding Billing Incomplete",
        description:
          "Merchant enabled disputes and automation but did not complete the billing step.",
      };
    case MerchantEmailTrigger.DISPUTE_ONBOARDING_AUTOMATION_DECLINED:
      return {
        label: "Dispute Onboarding Automation Declined",
        description:
          "Merchant enabled disputes but chose not to enable AI automation.",
      };
    default:
      assertNever(trigger);
  }
}

export interface MerchantEmailVariableMeta {
  name: string;
  description: string;
  example: string;
}

/**
 * Variables available to all merchant email triggers.
 * Use {{variableName}} syntax in HTML templates.
 */
export const MERCHANT_EMAIL_VARIABLES: readonly MerchantEmailVariableMeta[] = [
  {
    name: "teamId",
    description: "The team's unique identifier",
    example: "507f1f77bcf86cd799439011",
  },
  {
    name: "teamName",
    description: "The team's display name",
    example: "Acme Store",
  },
];

export function extractMerchantEmailVariables(template: string): string[] {
  const names = new Set<string>();
  const known = new Set(MERCHANT_EMAIL_VARIABLES.map((v) => v.name));
  for (const match of template.matchAll(/\{\{(\w+)\}\}/g)) {
    const name = match[1];
    if (name !== undefined && known.has(name)) {
      names.add(name);
    }
  }
  return [...names];
}

export function interpolateMerchantEmailTemplate(
  template: string,
  variables: Record<string, string>,
): string {
  return template.replace(
    /\{\{(\w+)\}\}/g,
    (match, key: string) => variables[key] ?? match,
  );
}

export function getMerchantEmailCampaignStatusMeta(
  status: MerchantEmailCampaignStatus,
): { label: string; badgeColor: "gray" | "success" | "warning" } {
  switch (status) {
    case MerchantEmailCampaignStatus.NEW:
      return { label: "New", badgeColor: "gray" };
    case MerchantEmailCampaignStatus.ACTIVE:
      return { label: "Active", badgeColor: "success" };
    case MerchantEmailCampaignStatus.ARCHIVED:
      return { label: "Archived", badgeColor: "warning" };
    default:
      assertNever(status);
  }
}
